import { useTranslation } from 'react-i18next';
import { Chip, Link, Typography } from '@mui/material';
import { alpha } from '@mui/material/styles';
import React from 'react';
import styled from 'styled-components';
import { Link as RouterLink } from 'react-router-dom';
import { UpcomingProductIngredientDto } from '@kotipizzagroup/kotipizza-products-api-client';
import { groupBy } from 'lodash';
import { formatDecimal, formatEuro } from '../../../shared/utils/formatters';
import { getConfiguration } from '../../../shared/utils/configuration';
import { cardStyles } from '../../RestaurantOverviewPage/styles';
import { S4DStatusIndicator } from '../../ProductsPage/components/S4DStatusIndicator';
import { S4DRecipeStatusIcon } from '../../ProductsPage/components/S4DRecipeStatusIcon';
import { belongsInS4DRecipe, S4DProductStatus } from '../../ProductsPage/utils/s4dProductComparison';
import { getS4DIngredientText } from '../../ProductsPage/utils/s4dIssueMessages';
import { FullCardData, OverviewProduct, OverviewSize } from './useUpcomingProductOverview';

const S4D_ADMIN_LABEL = 'S4D Admin';

type RecipeRow = {
  ingredientId: number;
  ingredientCategoryId?: number | null;
  name: string;
  quantity: number;
  isActive: boolean;
  dosings: { sizeId: number; grams: number }[];
};

type Props = FullCardData & {
  getIngredientName: (ingredientId: number) => string | null | undefined;
};

export const UpcomingProductCard: React.FC<Props> = ({
  product,
  comparison,
  isTestMenuOnly,
  isInMenu,
  hasSavedPrices,
  pricesFailed,
  getIngredientName,
}) => {
  const { t } = useTranslation();
  const s4dAdminBaseUrl = getConfiguration<string>('s4d.admin', '');

  const recipe = getRecipe(product);
  const missingCalories = findMissing(product, (ingredient) => ingredient.energy);
  const missingCarbon = findMissing(product, (ingredient) => ingredient.carbonFootprint);
  const isReady =
    isReadyToPublish(product, recipe, missingCalories, missingCarbon) &&
    isInMenu &&
    hasSavedPrices &&
    comparison?.status !== S4DProductStatus.LinkBroken;
  const s4dProduct = comparison?.s4dProducts[0];

  return (
    <Card>
      <Header>
        <ProductName to={`/products/${product.productId}`}>{product.name}</ProductName>
        {pricesFailed ? (
          <Pill $color="error">{t('products.upcoming.pricesFailed')}</Pill>
        ) : (
          isReady !== undefined && (
            <Pill $color={isReady ? 'success' : 'error'}>
              {isReady ? t('products.upcoming.readyToPublish') : t('products.upcoming.cantBePublished')}
            </Pill>
          )
        )}
      </Header>
      <SubLine>
        {product.categoryName || <Missing>{t('products.upcoming.informationMissing')}</Missing>}
        {isTestMenuOnly && <Chip size="small" label={t('products.upcoming.testMenu')} />}
      </SubLine>

      {product.description ? (
        <Description title={product.description}>{product.description}</Description>
      ) : (
        <Missing>
          {t('products.upcoming.description')}
          {t('products.upcoming.informationMissing')}
        </Missing>
      )}

      <SizeGrid>
        <HeaderCell>{t('products.upcoming.size')}</HeaderCell>
        <HeaderCell $right>{t('products.upcoming.price')}</HeaderCell>
        <HeaderCell $right>{t('products.upcoming.kcal')}</HeaderCell>
        <HeaderCell $right>{t('products.upcoming.co2')}</HeaderCell>
        <HeaderCell $right>{t('products.upcoming.pictures')}</HeaderCell>
        {product.sizes.map((size) => (
          <React.Fragment key={size.sizeId}>
            <Cell>{size.name}</Cell>
            <Cell $right>
              {size.price === undefined ? '–' : size.price > 0 ? formatEuro(size.price) : <Missing>–</Missing>}
            </Cell>
            <Cell $right>{Math.round(countKCalories(size))}</Cell>
            <Cell $right>{formatDecimal(countCarbonFootprint(size))}</Cell>
            <Cell $right>{size.pictureCount === 4 ? '4/4' : <Missing>{size.pictureCount}/4</Missing>}</Cell>
          </React.Fragment>
        ))}
      </SizeGrid>
      {!isInMenu && <Missing>{t('products.upcoming.notInMenu')}</Missing>}
      {!hasSavedPrices && <Missing>{t('products.upcoming.noSavedPrices')}</Missing>}
      {missingCalories.length > 0 && (
        <Missing>
          {t('products.upcoming.caloriesMissing')}
          {missingCalories.join(', ')}
        </Missing>
      )}
      {missingCarbon.length > 0 && (
        <Missing>
          {t('products.upcoming.carbonFootprintMissing')}
          {missingCarbon.join(', ')}
        </Missing>
      )}

      <SectionTitle>{t('products.upcoming.recipe')}</SectionTitle>
      <div>
        {recipe.map((row) => (
          <RecipeLine key={row.ingredientId}>
            <RecipeName>
              {/* eslint-disable-next-line i18next/no-literal-string */}
              <Quantity>{row.quantity} ×</Quantity>
              <IngredientLink to={`/ingredients/${row.ingredientId}`} $inactive={!row.isActive}>
                {row.name}
                {!row.isActive && ` ${t('products.upcoming.inactive')}`}
              </IngredientLink>
            </RecipeName>
            <Dosings>
              {row.dosings.map((dosing, index) => (
                <React.Fragment key={dosing.sizeId}>
                  {index > 0 && ' / '}
                  {/* eslint-disable-next-line i18next/no-literal-string */}
                  {dosing.grams > 0 ? `${dosing.grams}g` : <Missing as="span">–</Missing>}
                </React.Fragment>
              ))}
            </Dosings>
            {comparison ? (
              <S4DRecipeStatusIcon
                comparison={comparison}
                ingredientId={row.ingredientId}
                isCompared={
                  comparison.isRecipeCompared && belongsInS4DRecipe(row.ingredientCategoryId, product.categoryId ?? 0)
                }
                fontSize={16}
              />
            ) : (
              <MarkSpacer />
            )}
          </RecipeLine>
        ))}
      </div>
      {comparison && comparison.extraS4DToppings.length > 0 && (
        <Missing>
          {t('S4D recipe includes additional toppings')}{' '}
          {comparison.extraS4DToppings
            .map((extra) =>
              getS4DIngredientText({ ingredientId: extra.topping.ingredientId || undefined, name: extra.topping.name })
            )
            .join(', ')}
        </Missing>
      )}

      <Footer>
        {comparison && <S4DStatusIndicator comparison={comparison} getIngredientName={getIngredientName} />}
        {s4dProduct ? (
          <>
            {/* eslint-disable-next-line i18next/no-literal-string */}
            <span>S4D ID: {s4dProduct.s4dId}</span>
            <Link
              href={`${s4dAdminBaseUrl}/Content/Secure/Menu/Products/Details.aspx?product=${s4dProduct.s4dId}`}
              target="_blank"
              rel="noreferrer"
            >
              {S4D_ADMIN_LABEL}
            </Link>
          </>
        ) : (
          comparison && <Missing as="span">{t('S4D item not found')}</Missing>
        )}
      </Footer>
    </Card>
  );
};

const getRecipe = (product: OverviewProduct): RecipeRow[] => {
  const rows = product.sizes.flatMap((size) =>
    (size.ingredients ?? []).map((ingredient) => ({ ...ingredient, sizeId: size.sizeId ?? 0 }))
  );

  return Object.values(groupBy(rows, (row) => row.ingredientId ?? 0)).map((sizeRows) => ({
    ingredientId: sizeRows[0].ingredientId ?? 0,
    ingredientCategoryId: sizeRows[0].ingredientCategoryId,
    name: sizeRows[0].ingredientName ?? String(sizeRows[0].ingredientId),
    quantity: sizeRows[0].quantity ?? 0,
    isActive: Boolean(sizeRows[0].isActive),
    dosings: sizeRows.map((row) => ({ sizeId: row.sizeId, grams: row.dosing ?? 0 })),
  }));
};

const findMissing = (
  product: OverviewProduct,
  getValue: (ingredient: UpcomingProductIngredientDto) => number | null | undefined
): string[] => {
  const names = product.sizes.flatMap((size) =>
    (size.ingredients ?? [])
      .filter((ingredient) => !((getValue(ingredient) ?? 0) > 0))
      .map((ingredient) => `${ingredient.ingredientName} [${ingredient.ingredientId}]`)
  );

  return [...new Set(names)];
};

/** Undefined while the prices are still being calculated. */
const isReadyToPublish = (
  product: OverviewProduct,
  recipe: RecipeRow[],
  missingCalories: string[],
  missingCarbon: string[]
): boolean | undefined => {
  if (product.sizes.some((size) => size.price === undefined)) return undefined;

  return (
    Boolean(product.categoryName && product.description && product.name) &&
    missingCalories.length === 0 &&
    missingCarbon.length === 0 &&
    product.sizes.every((size) => size.pictureCount === 4 && (size.price ?? 0) > 0) &&
    recipe.every((row) => row.isActive && row.dosings.every((dosing) => dosing.grams > 0))
  );
};

const countKCalories = (size: OverviewSize): number => {
  const total = (size.ingredients ?? []).reduce(
    (sum, ingredient) => sum + ((ingredient.energy ?? 0) / 100) * (ingredient.dosing ?? 0),
    0
  );
  return Math.ceil(total * 100) / 100;
};

const countCarbonFootprint = (size: OverviewSize): number => {
  const total = (size.ingredients ?? []).reduce(
    (sum, ingredient) => sum + ((ingredient.carbonFootprint ?? 0) / 10) * (ingredient.dosing ?? 0),
    0
  );
  return Math.ceil(total * 100) / 100;
};

const Card = styled.div`
  ${cardStyles}
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing(1)};
  width: 340px;
  font-size: ${({ theme }) => theme.fontSizes.sm};

  ${({ theme }) => theme.breakpoints.down('sm')} {
    width: 100%;
  }
`;

const Header = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: ${({ theme }) => theme.spacing(1)};
`;

const ProductName = styled(RouterLink)`
  color: ${({ theme }) => theme.palette.text.primary};
  font-size: ${({ theme }) => theme.fontSizes.xl};
  font-weight: 700;
  line-height: 1.3;
  text-decoration: none;

  &:hover {
    color: ${({ theme }) => theme.palette.primary.main};
    text-decoration: underline;
  }
`;

const Pill = styled.span<{ $color: 'success' | 'error' }>`
  flex-shrink: 0;
  padding: 2px 8px;
  border-radius: 999px;
  font-size: ${({ theme }) => theme.fontSizes.xxs};
  font-weight: 700;
  white-space: nowrap;
  color: ${({ theme, $color }) => theme.palette[$color].main};
  background-color: ${({ theme, $color }) => alpha(theme.palette[$color].main, 0.14)};
`;

const SubLine = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing(1)};
  margin-top: -${({ theme }) => theme.spacing(0.5)};
  color: ${({ theme }) => theme.palette.text.secondary};
`;

const Description = styled.div`
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  color: ${({ theme }) => theme.palette.text.secondary};
`;

const SizeGrid = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 1fr) repeat(4, auto);
  column-gap: ${({ theme }) => theme.spacing(1.5)};
  padding-top: ${({ theme }) => theme.spacing(1)};
  border-top: 1px solid ${({ theme }) => theme.palette.divider};
`;

const HeaderCell = styled.div<{ $right?: boolean }>`
  padding-bottom: 2px;
  font-size: ${({ theme }) => theme.fontSizes.xxs};
  color: ${({ theme }) => theme.palette.text.secondary};
  text-align: ${({ $right }) => ($right ? 'right' : 'left')};
`;

const Cell = styled.div<{ $right?: boolean }>`
  padding: 2px 0;
  text-align: ${({ $right }) => ($right ? 'right' : 'left')};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const SectionTitle = styled(Typography).attrs({ variant: 'h6' })`
  padding-top: ${({ theme }) => theme.spacing(1)};
  border-top: 1px solid ${({ theme }) => theme.palette.divider};
`;

const RecipeLine = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing(1)};
  padding: 3px 0;

  & + & {
    border-top: 1px solid ${({ theme }) => theme.palette.divider};
  }
`;

const RecipeName = styled.div`
  display: flex;
  gap: 4px;
  flex: 1;
  min-width: 0;
`;

const Quantity = styled.span`
  flex-shrink: 0;
  font-weight: 700;
`;

const IngredientLink = styled(RouterLink)<{ $inactive: boolean }>`
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  text-decoration: none;
  color: ${({ theme, $inactive }) => ($inactive ? theme.palette.error.main : theme.palette.text.primary)};

  &:hover {
    text-decoration: underline;
  }
`;

const Dosings = styled.span`
  flex-shrink: 0;
  font-size: ${({ theme }) => theme.fontSizes.xs};
  color: ${({ theme }) => theme.palette.text.secondary};
`;

const MarkSpacer = styled.span`
  width: 16px;
`;

const Missing = styled.div`
  font-size: ${({ theme }) => theme.fontSizes.xs};
  color: ${({ theme }) => theme.palette.error.main};
`;

const Footer = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing(1)};
  margin-top: auto;
  padding-top: ${({ theme }) => theme.spacing(1)};
  border-top: 1px solid ${({ theme }) => theme.palette.divider};
  color: ${({ theme }) => theme.palette.text.secondary};
`;
